import SelectionSingleSelectGroup from '@/components/ui/SelectionSingleSelectGroup';
import DisclosureNumberFilter from '@/components/ui/DisclosureNumberFilter';
import { educatorFilterFields as filterFields, educatorFilterCountExclusions } from '@/pages/educators/filterConfig';
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { parse, stringify } from 'query-string';
import DialogTemplate from '@/dialogs_common/DialogTemplate';
import CloseDialogButton from '@/dialogs_common/CloseDialogButton';
import TagTypes from '@/constants/TagTypes';
import SearchFilter from '@/forms/_fields_v2/SearchFilter';
import DisclosureTagFilter from '@/forms/_fields_v2/DisclosureTagFilter';
import ClearAllFilters from '@/components/ClearAllFilters';
import { getSearchUtils } from '@/utils/query-utils';
import useFilterCount from '@/hooks/useFilterCount';
import { SolidButton } from '@/@communityshare/ui/Button';
import DisclosureFilter from '@/forms/_fields_v2/DisclosureFilter';
import _ from 'lodash';
import useNetworkFilter from './useNetworkFilter';
import SolidButtonLoader from '@/@communityshare/ui/Button/SolidButtonLoader';
import { tagNodes_tagType } from '@/utils/query-helpers';

export default function EducatorFiltersDialog(props) {
  const { useQuery, router, onClose } = props;

  const { location, history } = router;

  const [search, setSearch] = useState(parse(location.search));
  const [querySearch, setQuerySearch] = useState(parse(location.search));

  const debounceSetQuerySearch = useCallback(_.debounce(setQuerySearch, 250), []);

  const networkFilter = useNetworkFilter(search);

  const filterKeys = _.without(_.keys(filterFields), ...educatorFilterCountExclusions);
  const filterCount = useFilterCount(search, filterKeys);

  function onSubmit() {
    history.push({
      pathname: location.pathname,
      search: stringify({
        ...search,
        page: 1
      })
    });
    onClose();
  }

  function onChange(nextSearch) {
    setSearch(nextSearch);
    debounceSetQuerySearch(nextSearch);
  }

  // Used as an intermediate query to show potential match count as filters change
  function getTagParams(tag) {
    return {
      where: {
        filter: { ...networkFilter, intent: 'educator', [tagNodes_tagType(tag)]: [tag?.id] },
      },
      pagination: {
        pageSize: 0
      }
    };
  }

  const searchUtils = getSearchUtils(search, onChange);

  const result = useQuery({ search: querySearch });

  return (
    <DialogTemplate className="w-screen max-w-screen-sm h-[calc(100dvh-32px)] max-h-[calc(720px)]">
      <CloseDialogButton onClick={onClose} />
      <div className="flex flex-col space-y-8x h-full">
        <div className="space-y-1 p-6 border-b border-cs-gray-200">
          <h2 className="text-cs-gray-900 text-2xl font-semibold">
            Filters
          </h2>
          <div className="text-cs-gray-500 text-sm">
            Narrow your search results.
          </div>
        </div>
        <div className="px-6 py-8 space-y-8 overflow-y-auto h-full">
          <div className="space-y-4">
            <DisclosureFilter
              label="Search"
              value={[
                searchUtils(filterFields.name).value,
                searchUtils(filterFields.organization).value,
                searchUtils(filterFields.district).value,
                searchUtils(filterFields.searchEducator).value
              ]}
            >
              <SearchFilter
                label="Name"
                description="Find educators whose first or last name matches any of the words you enter."
                value={searchUtils(filterFields.name).value}
                onChange={searchUtils(filterFields.name).onChangeValue}
              />
              <SearchFilter
                label="School"
                description="Find educators affiliated with a school whose name contains the phrase you type."
                value={searchUtils(filterFields.organization).value}
                onChange={searchUtils(filterFields.organization).onChangeValue}
              />
              <SearchFilter
                label="District"
                description="Find educators affiliated with a school district whose name contains the phrase you type."
                value={searchUtils(filterFields.district).value}
                onChange={searchUtils(filterFields.district).onChangeValue}
              />
              <SearchFilter
                label="Profile"
                description="Find educators that have a specific phrase in their biography, or in the description of the subjects they teach."
                value={searchUtils(filterFields.searchEducator).value}
                onChange={searchUtils(filterFields.searchEducator).onChangeValue}
              />
            </DisclosureFilter>
          </div>
          <DisclosureTagFilter
            modelName="user"
            type={TagTypes.Grade}
            label="Grade levels"
            getParams={getTagParams}
            value={searchUtils(filterFields.grades).value}
            onChange={searchUtils(filterFields.grades).onChangeArray}
          />
          <DisclosureTagFilter
            modelName="user"
            type={TagTypes.Subject}
            label="Subjects"
            getParams={getTagParams}
            value={searchUtils(filterFields.subjects).value}
            onChange={searchUtils(filterFields.subjects).onChangeArray}
          />
          <DisclosureTagFilter
            modelName="user"
            type={TagTypes.Practice}
            label="Teaching practices"
            getParams={getTagParams}
            value={searchUtils(filterFields.practices).value}
            onChange={searchUtils(filterFields.practices).onChangeArray}
          />
          <DisclosureNumberFilter
            label="Projects/Events created"
            operatorValue={searchUtils(filterFields.createdOperator).value}
            onOperatorChange={searchUtils(filterFields.createdOperator).onChangeValue}
            numberValue={searchUtils(filterFields.created).value}
            onNumberChange={searchUtils(filterFields.created).onChangeValue}
            numberLabel="This many created"
            numberDescription={`Educators with ${searchUtils(filterFields.createdOperator).value} this many projects or events created.`}
          />

          <DisclosureNumberFilter
            label="Projects/Events completed"
            operatorValue={searchUtils(filterFields.completedOperator).value}
            onOperatorChange={searchUtils(filterFields.completedOperator).onChangeValue}
            numberValue={searchUtils(filterFields.completed).value}
            onNumberChange={searchUtils(filterFields.completed).onChangeValue}
            numberLabel="This many completed"
            numberDescription={`Educators with ${searchUtils(filterFields.completedOperator).value} this many projects or events completed.`}
          />

          <DisclosureFilter
            label="Joined"
            value={[
              searchUtils(filterFields.joined).value
            ]}
          >
            <SelectionSingleSelectGroup
              label="Joined within the last ..."
              value={searchUtils(filterFields.joined).value}
              onChange={searchUtils(filterFields.joined).onChangeValue}
              onClear={() => setSearch(_.omit(search, 'joined'))}
              options={['1 week', '2 weeks', '1 month', '2 months', '3 months', '6 months', 'year']}
            />
          </DisclosureFilter>
        </div>
        <div className="p-6 flex items-center justify-between border-t border-cs-gray-200">
          <ClearAllFilters
            show={true}
            className="hover:opacity-70"
            onClick={() => setSearch(_.omit(search, _.keys(filterFields)))}
            count={filterCount}
          />
          {result._isPreviousData ? (
            <SolidButton onClick={onSubmit}>
              <SolidButtonLoader />
            </SolidButton>
          ) : (
            <SolidButton onClick={onSubmit}>
              Show {filterCount > 0 ? result.meta?.totalCount : ''} results
            </SolidButton>
          )}
        </div>
      </div>
    </DialogTemplate>
  );
}

EducatorFiltersDialog.propTypes = {
  useQuery: PropTypes.func.isRequired,
  router: PropTypes.shape({
    location: PropTypes.shape({
      search: PropTypes.string,
      pathname: PropTypes.string,
    }),
    history: PropTypes.shape({
      push: PropTypes.func,
    }),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};
